import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { gameSessionService } from '../../services/api/gameSessionService';
import './DoodleBoard.css';

/**
 * Doodle Date — draw-and-guess rounds on a shared canvas (GameKind "doodle").
 *
 * Server-authoritative (RoomHub.Games.cs DoodleRules): rounds, drawer rotation, the secret
 * word, guess checking and scores all live in the game_state broadcast. This component only
 * renders state and sends deltas. The DRAWER's canvas is local truth for the round (no
 * flicker); everyone else replays the relayed stroke list. The word rides state like
 * Two-Truths' lieIndex — shown only to the drawer.
 *
 * Stroke wire format (one string per stroke): "colorIdx|size|x1,y1,x2,y2,..." — logical
 * 640×420 space, integer coords.
 */

interface DoodleGame {
  gameId: string;
  status: string;
  note?: string;
  state: string;
  winners?: string[];
}

interface DoodleState {
  phase?: string; // lobby | draw | reveal
  round?: number;
  totalRounds?: number;
  drawerId?: string;
  word?: string;
  blanks?: string;
  deadline?: string;
  strokes?: string[];
  scores?: Record<string, number>;
  guessed?: Record<string, boolean>;
  lastGuess?: { by: string; text: string } | null;
  participants?: string[];
  roster?: { id: string; name: string }[];
}

const W = 640;
const H = 420;
const INK = ['#1b1f2a', '#e11d48', '#2563eb', '#16a34a', '#f59e0b', '#8b5cf6']; // paint, not chrome
const SIZES = [4, 10];

function drawStroke(g: CanvasRenderingContext2D, s: string) {
  const parts = s.split('|');
  if (parts.length < 3) return;
  const color = INK[Math.max(0, Math.min(INK.length - 1, parseInt(parts[0], 10) || 0))];
  const size = Math.max(1, Math.min(24, parseInt(parts[1], 10) || 4));
  const pts = parts[2].split(',').map((n) => parseInt(n, 10));
  if (pts.length < 2) return;
  g.strokeStyle = color;
  g.lineWidth = size;
  g.lineCap = 'round';
  g.lineJoin = 'round';
  g.beginPath();
  g.moveTo(pts[0], pts[1]);
  if (pts.length === 2) g.lineTo(pts[0] + 0.1, pts[1] + 0.1); // dot
  for (let i = 2; i + 1 < pts.length; i += 2) g.lineTo(pts[i], pts[i + 1]);
  g.stroke();
}

function fmtClock(ms: number): string {
  const s = Math.max(0, Math.ceil(ms / 1000));
  return `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, '0')}`;
}

export function DoodleBoard({ roomId, game, userId }: { roomId: string; game: DoodleGame; userId?: string }) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [colorIdx, setColorIdx] = useState(0);
  const [sizeIdx, setSizeIdx] = useState(0);
  const [guess, setGuess] = useState('');
  const [now, setNow] = useState(() => Date.now());
  const [collapsed, setCollapsed] = useState(false);

  // Drawer-local strokes for the current round (local truth — no relay flicker).
  const localStrokesRef = useRef<string[]>([]);
  const pendingRef = useRef<number[]>([]); // in-progress pointer points
  const drawingRef = useRef(false);
  const roundRef = useRef(-1);

  const st = useMemo<DoodleState>(() => {
    try {
      return JSON.parse(game.state) as DoodleState;
    } catch {
      return {};
    }
  }, [game.state]);

  const phase = game.status === 'waiting' ? 'waiting' : game.status !== 'active' ? 'over' : st.phase ?? 'lobby';
  const isDrawer = !!userId && st.drawerId === userId;
  const joined = !!userId && (st.participants ?? []).includes(userId);
  const youGuessed = !!userId && !!st.guessed?.[userId];
  const deadlineMs = st.deadline ? Date.parse(st.deadline) : 0;

  const nameOf = useCallback(
    (uid?: string) => (uid && st.roster?.find((r) => r.id === uid)?.name) || 'Artist',
    [st.roster],
  );

  const send = useCallback(
    (action: string, payload?: Record<string, unknown>) => {
      void gameSessionService.sendDelta(roomId, game.gameId, action, payload);
    },
    [roomId, game.gameId],
  );

  // Clock tick while a deadline is live.
  useEffect(() => {
    if (phase !== 'draw' && phase !== 'reveal') return;
    const id = window.setInterval(() => setNow(Date.now()), 500);
    return () => window.clearInterval(id);
  }, [phase]);

  // New round → clear local canvas state.
  useEffect(() => {
    if (st.round !== undefined && st.round !== roundRef.current) {
      roundRef.current = st.round;
      localStrokesRef.current = [];
      pendingRef.current = [];
      setGuess('');
    }
  }, [st.round]);

  // Repaint: drawer renders local truth; guessers replay the relayed strokes.
  useEffect(() => {
    const canvas = canvasRef.current;
    const g = canvas?.getContext('2d');
    if (!canvas || !g) return;
    g.fillStyle = '#fdfcf8';
    g.fillRect(0, 0, W, H);
    const strokes = isDrawer && phase === 'draw' ? localStrokesRef.current : st.strokes ?? [];
    for (const s of strokes) drawStroke(g, s);
    const pend = pendingRef.current;
    if (isDrawer && pend.length >= 2) drawStroke(g, `${colorIdx}|${SIZES[sizeIdx]}|${pend.join(',')}`);
  });

  // ---- Drawer pointer handling (logical-space coords). ----
  const toLocal = (e: React.PointerEvent): [number, number] => {
    const rect = (e.target as HTMLCanvasElement).getBoundingClientRect();
    return [
      Math.round(((e.clientX - rect.left) / rect.width) * W),
      Math.round(((e.clientY - rect.top) / rect.height) * H),
    ];
  };

  const flushStroke = useCallback(() => {
    const pts = pendingRef.current;
    if (pts.length < 2) {
      pendingRef.current = [];
      return;
    }
    const d = `${colorIdx}|${SIZES[sizeIdx]}|${pts.join(',')}`;
    localStrokesRef.current = [...localStrokesRef.current, d];
    pendingRef.current = [];
    if (d.length <= 590) send('stroke', { d });
  }, [colorIdx, sizeIdx, send]);

  const onDown = (e: React.PointerEvent) => {
    if (!isDrawer || phase !== 'draw') return;
    (e.target as HTMLCanvasElement).setPointerCapture(e.pointerId);
    drawingRef.current = true;
    pendingRef.current = [...toLocal(e)];
  };
  const onMove = (e: React.PointerEvent) => {
    if (!drawingRef.current) return;
    const [x, y] = toLocal(e);
    const pts = pendingRef.current;
    const lx = pts[pts.length - 2];
    const ly = pts[pts.length - 1];
    if (Math.abs(x - lx) + Math.abs(y - ly) < 3) return; // decimate
    pts.push(x, y);
    if (pts.length >= 120) {
      flushStroke();
      pendingRef.current = [x, y];
    }
    setNow(Date.now()); // trigger repaint
  };
  const onUp = () => {
    if (!drawingRef.current) return;
    drawingRef.current = false;
    flushStroke();
  };

  const clearCanvas = () => {
    localStrokesRef.current = [];
    pendingRef.current = [];
    send('clear');
    setNow(Date.now());
  };

  const submitGuess = () => {
    const text = guess.trim();
    if (!text) return;
    send('guess', { text });
    setGuess('');
  };

  const scoreRows = Object.entries(st.scores ?? {}).sort((a, b) => b[1] - a[1]);

  if (collapsed) {
    return (
      <button className="doodle-strip" onClick={() => setCollapsed(false)}>
        🎨 Doodle Date — round {st.round ?? 0}/{st.totalRounds ?? 0}
        {phase === 'draw' && deadlineMs ? ` · ${fmtClock(deadlineMs - now)}` : ''} · tap to open
      </button>
    );
  }

  return (
    <div className="doodle-board">
      <div className="doodle-head">
        <span className="doodle-title">🎨 Doodle Date</span>
        {phase === 'draw' || phase === 'reveal' ? (
          <span className="doodle-round">
            Round {st.round}/{st.totalRounds}
            {deadlineMs > 0 && <b className="doodle-clock"> {fmtClock(deadlineMs - now)}</b>}
          </span>
        ) : null}
        <button className="doodle-min" onClick={() => setCollapsed(true)} aria-label="Minimize">
          —
        </button>
      </div>

      {phase === 'waiting' && (
        <div className="doodle-card">
          <p>One of you draws, the other guesses. Six rounds, big laughs, zero art skills required.</p>
          <button className="doodle-cta" onClick={() => send('start')}>
            ▶ Start Doodle Date
          </button>
        </div>
      )}

      {phase === 'lobby' && (
        <div className="doodle-card">
          <p>
            Artists in: <b>{(st.participants ?? []).map((p) => nameOf(p)).join(', ') || '—'}</b>
          </p>
          {!joined && (
            <button className="doodle-cta" onClick={() => send('join')}>
              ✋ Join in
            </button>
          )}
          {joined && (
            <button className="doodle-cta" onClick={() => send('begin')}>
              🚀 Begin — everyone’s in
            </button>
          )}
          <p className="doodle-hint">{game.note}</p>
        </div>
      )}

      {(phase === 'draw' || phase === 'reveal' || phase === 'over') && (
        <>
          <div className="doodle-status">
            {phase === 'draw' &&
              (isDrawer ? (
                <span>
                  ✏️ You’re drawing: <b className="doodle-word">{st.word}</b> — don’t say it out loud!
                </span>
              ) : (
                <span>
                  {nameOf(st.drawerId)} is drawing · <b className="doodle-word">{st.blanks}</b>
                  {youGuessed && ' · you got it! 🎉'}
                </span>
              ))}
            {phase === 'reveal' && <span>{game.note}</span>}
            {phase === 'over' && <span>🏁 {game.note}</span>}
          </div>

          <canvas
            ref={canvasRef}
            className={`doodle-canvas${isDrawer && phase === 'draw' ? ' drawing' : ''}`}
            width={W}
            height={H}
            onPointerDown={onDown}
            onPointerMove={onMove}
            onPointerUp={onUp}
            onPointerCancel={onUp}
          />

          {phase === 'draw' && isDrawer && (
            <div className="doodle-tools">
              {INK.map((c, i) => (
                <button
                  key={c}
                  className={`doodle-ink${i === colorIdx ? ' on' : ''}`}
                  style={{ background: c }}
                  onClick={() => setColorIdx(i)}
                  aria-label={`Color ${i + 1}`}
                />
              ))}
              <button className={`doodle-size${sizeIdx === 0 ? ' on' : ''}`} onClick={() => setSizeIdx(0)}>
                ●
              </button>
              <button className={`doodle-size big${sizeIdx === 1 ? ' on' : ''}`} onClick={() => setSizeIdx(1)}>
                ●
              </button>
              <button className="doodle-clear" onClick={clearCanvas}>
                🗑 Clear
              </button>
            </div>
          )}

          {phase === 'draw' && !isDrawer && joined && !youGuessed && (
            <div className="doodle-guess">
              <input
                value={guess}
                onChange={(e) => setGuess(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && submitGuess()}
                placeholder="Type your guess…"
                maxLength={40}
              />
              <button className="doodle-cta" onClick={submitGuess}>
                Guess
              </button>
            </div>
          )}
          {phase === 'draw' && !joined && (
            <button className="doodle-cta" onClick={() => send('join')}>
              ✋ Jump in and guess
            </button>
          )}

          {st.lastGuess && phase === 'draw' && (
            <div className="doodle-feed">
              {nameOf(st.lastGuess.by)}: “{st.lastGuess.text}”
            </div>
          )}

          {(phase === 'reveal' || phase === 'over') && scoreRows.length > 0 && (
            <div className="doodle-scores">
              {scoreRows.map(([uid, pts]) => (
                <span key={uid} className="doodle-score-chip">
                  {nameOf(uid)} <b>{pts}</b>
                </span>
              ))}
            </div>
          )}

          {phase === 'reveal' && (
            <button className="doodle-cta" onClick={() => send('next')}>
              Next round →
            </button>
          )}
        </>
      )}
    </div>
  );
}
